import { DatePipe, JsonPipe, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { SharedModule } from '@src/app/shared/shared.module';

@Component({
  selector: 'app-calendar-lab',
  templateUrl: './calendar-lab.component.html',
  styleUrls: ['./calendar-lab.component.scss'],
  imports: [SharedModule, FormsModule, ReactiveFormsModule, JsonPipe, DatePipe, NgIf],
})
export class CalendarLabComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  private logSequence = 0;
  private previousFormValue: Record<string, unknown> | null = null;

  minDate = this.dateFormatterService.parseDateTruncateTime('2026-01-01') as Date;
  maxDate = this.dateFormatterService.parseDateTruncateTime('2026-12-31') as Date;

  ngModelSingleValue: string | null = null;
  ngModelMonthYearValue: string | null = '2026-02-01';
  ngModelRangeValue: string[] = [];
  ngModelTimeOnlyValue: string | null = null;
  ngModelDelayedValue: string | null = '2026-03-15';

  delayedDateLoading = true;
  delayedMinDate: Date | null = null;
  delayedMaxDate: Date | null = null;

  form: FormGroup;

  logs: string[] = [];
  scenarioLogs: Record<string, string[]> = {
    'ngModel-single': [],
    'ngModel-month-year': [],
    'ngModel-range': [],
    'ngModel-time-only': [],
    'ngModel-delayed': [],
    'form-single': [],
    'form-range': [],
    'form-month-year': [],
    'form-weekend-holiday': [],
    'form-auto': [],
  };

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly fb: FormBuilder,
    private readonly dateFormatterService: DateFormatterService
  ) {
    this.form = this.fb.group({
      single: ['2026-05-20'],
      range: [['2026-06-01', '2026-06-15']],
      monthYear: ['2026-02-01'],
      weekendHoliday: [null],
      autoSingle: ['2026-07-10'],
    });
  }

  ngOnInit(): void {
    this.log('page:init');

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.log('form:valueChanges', value);
      this.logFormControlChanges(value as Record<string, unknown>);
    });

    this.form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(status => {
      this.log('form:statusChanges', { status });
    });

    this.log('scenario:delayed-limits:start', { value: this.ngModelDelayedValue });
    this.logToScenario('ngModel-delayed', 'scenario:start', { value: this.ngModelDelayedValue });

    setTimeout(() => {
      this.delayedMinDate = this.dateFormatterService.parseDateTruncateTime('2026-03-01') as Date;
      this.delayedMaxDate = this.dateFormatterService.parseDateTruncateTime('2026-09-30') as Date;
      this.delayedDateLoading = false;
      this.log('scenario:delayed-limits:loaded', {
        minDate: this.ngModelDateForLog(this.delayedMinDate),
        maxDate: this.ngModelDateForLog(this.delayedMaxDate),
      });
      this.logToScenario('ngModel-delayed', 'scenario:limits-loaded', {
        minDate: this.ngModelDateForLog(this.delayedMinDate),
        maxDate: this.ngModelDateForLog(this.delayedMaxDate),
      });
      this.cdr.detectChanges();
    }, 1800);
  }

  clearLogs(): void {
    this.logs = [];
    this.logSequence = 0;
    this.clearScenarioLogs();
    this.log('logs:cleared');
  }

  resetAllStates(): void {
    this.ngModelSingleValue = null;
    this.ngModelMonthYearValue = '2026-02-01';
    this.ngModelRangeValue = [];
    this.ngModelTimeOnlyValue = null;
    this.ngModelDelayedValue = '2026-03-15';
    this.delayedDateLoading = true;
    this.delayedMinDate = null;
    this.delayedMaxDate = null;

    this.form.reset({
      single: '2026-05-20',
      range: ['2026-06-01', '2026-06-15'],
      monthYear: '2026-02-01',
      weekendHoliday: null,
      autoSingle: '2026-07-10',
    });

    this.log('actions:reset-all');

    setTimeout(() => {
      this.delayedMinDate = this.dateFormatterService.parseDateTruncateTime('2026-03-01') as Date;
      this.delayedMaxDate = this.dateFormatterService.parseDateTruncateTime('2026-09-30') as Date;
      this.delayedDateLoading = false;
      this.log('scenario:delayed-limits:loaded-after-reset', {
        minDate: this.ngModelDateForLog(this.delayedMinDate),
        maxDate: this.ngModelDateForLog(this.delayedMaxDate),
      });
      this.logToScenario('ngModel-delayed', 'scenario:limits-loaded-after-reset', {
        minDate: this.ngModelDateForLog(this.delayedMinDate),
        maxDate: this.ngModelDateForLog(this.delayedMaxDate),
      });
      this.cdr.detectChanges();
    }, 1800);
  }

  onNgModelChange(name: string, value: unknown): void {
    this.log(`ngModelChange:${name}`, value);
    this.logToScenario(name, 'ngModelChange', value);
  }

  onOutput(name: string, value?: unknown): void {
    this.log(`output:${name}`, value);
    const scenario = this.extractScenarioName(name);
    this.logToScenario(scenario, `output:${name}`, value);
  }

  getScenarioLogText(scenario: string): string {
    const scenarioLogs = this.scenarioLogs[scenario] ?? [];
    return scenarioLogs.length > 0 ? scenarioLogs.join('\n') : 'Sem eventos neste cenário.';
  }

  private ngModelDateForLog(date: Date | null): string | null {
    if (!date) return null;
    return this.dateFormatterService.formatDateToYYYYMMDD(date);
  }

  private log(event: string, payload?: unknown): void {
    this.logSequence += 1;
    const prefix = `[${this.logSequence}] ${event}`;
    const suffix = payload === undefined ? '' : ` ${JSON.stringify(payload)}`;
    this.logs = [...this.logs, `${prefix}${suffix}`];
  }

  private clearScenarioLogs(): void {
    Object.keys(this.scenarioLogs).forEach(key => {
      this.scenarioLogs[key] = [];
    });
  }

  private extractScenarioName(eventName: string): string {
    const [scenario] = eventName.split('.');
    return scenario;
  }

  private logToScenario(scenario: string, event: string, payload?: unknown): void {
    if (!this.scenarioLogs[scenario]) {
      this.scenarioLogs[scenario] = [];
    }

    this.logSequence += 1;
    const prefix = `[${this.logSequence}] ${event}`;
    const suffix = payload === undefined ? '' : ` ${JSON.stringify(payload)}`;
    this.scenarioLogs[scenario] = [...this.scenarioLogs[scenario], `${prefix}${suffix}`];
  }

  private logFormControlChanges(currentValue: Record<string, unknown>): void {
    const mapping: Record<string, string> = {
      single: 'form-single',
      range: 'form-range',
      monthYear: 'form-month-year',
      weekendHoliday: 'form-weekend-holiday',
      autoSingle: 'form-auto',
    };

    if (!this.previousFormValue) {
      this.previousFormValue = { ...currentValue };
      return;
    }

    Object.entries(mapping).forEach(([controlName, scenario]) => {
      const current = currentValue[controlName];
      const previous = this.previousFormValue?.[controlName];
      if (JSON.stringify(current) !== JSON.stringify(previous)) {
        this.logToScenario(scenario, `formControl:${controlName}.valueChanges`, current);
      }
    });

    this.previousFormValue = { ...currentValue };
  }
}
